import type { ConfigType } from '@plone/registry';
import type { BlockConfigBase } from '@plone/types';

import MainImageBlockInfo from '../components/Blocks/MainImageBlock';
import MultiButtonsBlockInfo from '../components/Blocks/MultiButtons';
import MainContentReaderBlockInfo from '../components/Blocks/MainContentReader';
import CarouselTemplate from '../components/Blocks/Listing/CarouselTemplate';
import MediaCarouselTemplate from '../components/Blocks/Listing/MediaCarouselTemplate';
import TeaserTemplate from '../components/Blocks/Listing/TeaserTemplate';
import {
  carouselSchemaEnhancer,
  mediaCarouselSchemaEnhancer,
  teaserSchemaEnhancer,
} from '../components/Blocks/Listing/schema';

declare module '@plone/types' {
  export interface BlocksConfigData {
    multiButtons: BlockConfigBase;
    mainContentReader: BlockConfigBase;
    mainImageBlock: BlockConfigBase;
  }
}

const customThemes = [
  {
    style: {
      '--theme-color': 'var(--block-theme-default-bg)',
      '--theme-foreground-color': 'var(--block-theme-default-text)',
      '--theme-high-contrast-foreground-color':
        'var(--block-theme-default-high-contrast)',
      '--theme-low-contrast-foreground-color':
        'var(--block-theme-default-low-contrast)',

      '--theme-high-contrast-color': 'var(--block-theme-default-high-bg)',
      '--theme-top-foreground-color': 'var(--block-theme-default-top-text)',
      '--theme-top-high-contrast-foreground-color':
        'var(--block-theme-default-top-high-contrast)',
      '--theme-top-low-contrast-foreground-color':
        'var(--block-theme-default-top-low-contrast)',
      '--theme-border-color': 'var(--block-theme-default-border)',
    },
    name: 'default',
    label: 'Default',
  },
  {
    style: {
      '--theme-color': 'var(--block-theme-alt-bg)',
      '--theme-foreground-color': 'var(--block-theme-alt-text)',
      '--theme-high-contrast-foreground-color':
        'var(--block-theme-alt-high-contrast)',
      '--theme-low-contrast-foreground-color':
        'var(--block-theme-alt-low-contrast)',

      '--theme-high-contrast-color': 'var(--block-theme-alt-high-bg)',
      '--theme-top-foreground-color': 'var(--block-theme-alt-top-text)',
      '--theme-top-high-contrast-foreground-color':
        'var(--block-theme-alt-top-high-contrast)',
      '--theme-top-low-contrast-foreground-color':
        'var(--block-theme-alt-top-low-contrast)',
      '--theme-border-color': 'var(--block-theme-alt-border)',
    },
    name: 'alt',
    label: 'Alternativo',
  },
];

function installBlocks(config: ConfigType) {
  config.blocks.blocksConfig.multiButtons = MultiButtonsBlockInfo;
  config.blocks.blocksConfig.mainImageBlock = MainImageBlockInfo;
  config.blocks.blocksConfig.mainContentReader = MainContentReaderBlockInfo;

  if ((config.blocks.blocksConfig as any).slider) {
    (config.blocks.blocksConfig as any).slider.imageScale = 'huge';
  }

  if ((config.blocks.blocksConfig as any).teaser) {
    (config.blocks.blocksConfig as any).teaser.imageScale = 'huge';
  }

  return config;
}

function installThemes(config: ConfigType) {
  config.blocks.themes = customThemes;

  return config;
}

function defaultBlocks(config: ConfigType) {
  config.blocks.initialBlocks = {
    ...config.blocks.initialBlocks,
    'News Item': [
      { '@type': 'title', fixed: true, required: true },
      { '@type': 'description', fixed: true, required: true },
      {
        '@type': 'mainImageBlock',
        align: 'center',
        theme: 'default',
        style: {
          'blockWidth:noprefix': {
            '--block-width': 'var(--default-container-width)',
          },
        },
        size: 'l',
      },
      { '@type': 'slate' },
    ],
    Galeria: [
      { '@type': 'title', fixed: true, required: true },
      {
        '@type': 'listing',
        headlineTag: 'h2',
        querystring: {
          query: [
            {
              i: 'portal_type',
              o: 'plone.app.querystring.operation.selection.any',
              v: ['Image'],
            },
            {
              i: 'path',
              o: 'plone.app.querystring.operation.string.relativePath',
              v: './',
            },
          ],
          sort_on: 'getObjPositionInParent',
          sort_order: 'ascending',
        },
        styles: {},
        theme: 'default',
        variation: 'imageGallery',
      },
      { '@type': 'slate' },
    ],
  };

  return config;
}

export default function install(config: ConfigType) {
  installThemes(config);
  installBlocks(config);
  defaultBlocks(config);

  // Listing: add a media carousel variation
  if ((config.blocks.blocksConfig as any).listing?.variations) {
    let variations = (config.blocks.blocksConfig as any).listing.variations;
    const hasCarousel = variations.some((v: any) => v.id === 'carousel');
    if (!hasCarousel) {
      variations = [
        ...variations,
        {
          id: 'carousel',
          title: 'Carrossel',
          template: CarouselTemplate,
          schemaEnhancer: carouselSchemaEnhancer,
        },
      ];
    }
    const hasMediaCarousel = variations.some(
      (v: any) => v.id === 'mediaCarousel',
    );
    if (!hasMediaCarousel) {
      variations = [
        ...variations,
        {
          id: 'mediaCarousel',
          title: 'Carrossel de Mídia',
          template: MediaCarouselTemplate,
          schemaEnhancer: mediaCarouselSchemaEnhancer,
        },
      ];
    }
    const hasTeaser = variations.some((v: any) => v.id === 'teaser');
    if (!hasTeaser) {
      variations = [
        ...variations,
        {
          id: 'teaser',
          title: 'Destaque',
          template: TeaserTemplate,
          schemaEnhancer: teaserSchemaEnhancer,
        },
      ];
    }
    (config.blocks.blocksConfig as any).listing.variations = variations;
  }

  // Allow local blocks inside gridBlock and ensure it uses our custom themes.
  if ((config.blocks.blocksConfig as any).gridBlock) {
    (config.blocks.blocksConfig as any).gridBlock.themes = customThemes;
    (config.blocks.blocksConfig as any).gridBlock.blocksConfig = {
      ...(config.blocks.blocksConfig as any).gridBlock.blocksConfig,
      themes: customThemes,
    };

    const localBlocks = [
      'multiButtons',
      'mainContentReader',
      '__button',
      'listing',
      'slider',
      'carousel',
    ];
    const gridBlock = (config.blocks.blocksConfig as any).gridBlock;
    if (gridBlock.allowedBlocks && gridBlock.blocksConfig) {
      gridBlock.allowedBlocks = [...gridBlock.allowedBlocks, ...localBlocks];
      localBlocks.forEach((blockId) => {
        gridBlock.blocksConfig[blockId] = (config.blocks.blocksConfig as any)[
          blockId
        ];
      });
    }
  }

  return config;
}
